import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { authHeader } from "@/lib/token";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CreditCard, Download, FileText, Loader2, Package, RefreshCw, Send } from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "");
const euro = (v) => `${(Number(v) || 0).toFixed(2).replace(".", ",")} €`;
const card = "bg-[#0f1019] border border-white/[0.07] rounded-2xl p-5 sm:p-6";

/** Paramètres > Abonnements & factures : produits (renouvellement, transfert) et factures PDF. */
export default function SettingsBilling() {
  const [bots, setBots] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [cancelBot, setCancelBot] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [transferBot, setTransferBot] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/bots`, { headers: authHeader() }).then((r) => setBots(r.data || [])).catch(() => setBots([]));
    axios.get(`${API}/invoices`, { headers: authHeader() }).then((r) => setInvoices(r.data || [])).catch(() => setInvoices([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const download = async (invoice) => {
    setDownloading(invoice.id);
    try {
      const r = await axios.get(`${API}/invoices/${invoice.id}/pdf`, { headers: authHeader(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `facture-${invoice.number || invoice.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement impossible");
    } finally {
      setDownloading(null);
    }
  };

  const confirmCancel = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/bots/${cancelBot.id}/subscription/cancel`, {}, { headers: authHeader() });
      toast.success("Abonnement arrêté : le produit reste actif jusqu'à la fin de la période payée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'arrêter l'abonnement");
    } finally {
      setCancelling(false);
      setCancelBot(null);
    }
  };

  const loadingBlock = <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-white/30" /></div>;

  return (
    <div className="space-y-6" data-testid="settings-billing">
      <div className={card}>
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Package className="w-5 h-5 text-amber-400" />Mes abonnements</h2>
        <p className="text-white/40 text-sm mb-5">Arrête un abonnement payé chaque mois ou donne un produit à quelqu'un d'autre.</p>
        {bots === null ? loadingBlock : bots.length === 0 ? (
          <p className="text-sm text-white/40" data-testid="billing-no-products">Tu n'as encore aucun produit.</p>
        ) : (
          <div className="space-y-2.5">
            {bots.map((bot) => {
              const expired = bot.expires_at && new Date(bot.expires_at) < new Date();
              const lifetime = bot.plan_id === "lifetime";
              return (
                <div key={bot.id} className="p-3.5 bg-black/20 border border-white/5 rounded-xl" data-testid={`billing-product-${bot.id}`}>
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="flex-1 min-w-[180px]">
                      <p className="text-sm font-medium">{bot.bot_name || bot.bot_type_name}</p>
                      <p className="text-xs text-white/40 mt-0.5">
                        {bot.bot_type_name}{bot.plan_name ? ` · ${bot.plan_name}` : ""}{bot.is_trial ? " · essai gratuit" : ""}
                      </p>
                      <p className="text-xs mt-1.5">
                        {expired ? <span className="text-red-400">Expiré le {fmtDate(bot.expires_at)}</span>
                          : lifetime ? <span className="text-emerald-400">À vie</span>
                          : bot.auto_renew ? <span className="text-blue-300 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" />Renouvelé automatiquement le {fmtDate(bot.expires_at)}</span>
                          : <span className="text-white/50">Actif jusqu'au {fmtDate(bot.expires_at)} (pas de renouvellement automatique)</span>}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {bot.auto_renew && (
                        <Button size="sm" variant="outline" onClick={() => setCancelBot(bot)} data-testid={`billing-cancel-${bot.id}`}
                          className="rounded-lg border-red-500/30 text-red-300 hover:bg-red-500/10 hover:text-red-200 text-xs">Arrêter l'abonnement</Button>
                      )}
                      {!bot.is_trial && !expired && (
                        <Button size="sm" variant="outline" onClick={() => setTransferBot(bot)} data-testid={`billing-transfer-${bot.id}`}
                          className="rounded-lg border-white/10 text-white/80 hover:bg-white/5 text-xs"><Send className="w-3.5 h-3.5 mr-1.5" />Transférer</Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className={card}>
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><FileText className="w-5 h-5 text-blue-400" />Factures</h2>
        <p className="text-white/40 text-sm mb-5">Toutes tes factures, à télécharger en PDF.</p>
        {invoices === null ? loadingBlock : invoices.length === 0 ? (
          <p className="text-sm text-white/40" data-testid="billing-no-invoices">Aucune facture pour l'instant.</p>
        ) : (
          <div className="divide-y divide-white/5" data-testid="billing-invoices">
            {invoices.map((inv) => (
              <div key={inv.id} className="py-3 flex flex-wrap items-center gap-3">
                <CreditCard className="w-4 h-4 text-white/30 shrink-0" />
                <div className="flex-1 min-w-[160px]">
                  <p className="text-sm">{inv.product} — {inv.plan}{inv.renewal ? " (renouvellement)" : ""}</p>
                  <p className="text-xs text-white/35">{inv.number} · {fmtDate(inv.date)}</p>
                </div>
                <span className="text-sm font-semibold">{euro(inv.amount)}</span>
                <Button size="sm" variant="outline" onClick={() => download(inv)} disabled={downloading === inv.id} data-testid={`billing-invoice-${inv.id}`}
                  className="rounded-lg border-white/10 text-xs">
                  {downloading === inv.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Download className="w-3.5 h-3.5 mr-1.5" />PDF</>}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!cancelBot} onOpenChange={(o) => !o && setCancelBot(null)}>
        <AlertDialogContent className="bg-[#12121a] border-white/10 rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Arrêter l'abonnement ?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/50">
              Tu ne seras plus prélevé pour <span className="text-white">{cancelBot?.bot_name}</span>. Le produit reste actif jusqu'au {fmtDate(cancelBot?.expires_at)}, puis s'arrête. Un e-mail te confirme la résiliation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg border-white/10">Garder l'abonnement</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel} disabled={cancelling} data-testid="billing-confirm-cancel" className="rounded-lg bg-red-600 hover:bg-red-500">
              {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Arrêter l'abonnement"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {transferBot && <TransferDialog bot={transferBot} onClose={() => setTransferBot(null)} onDone={() => { setTransferBot(null); load(); }} />}
    </div>
  );
}

function TransferDialog({ bot, onClose, onDone }) {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [newKey, setNewKey] = useState(true);
  const [sending, setSending] = useState(false);
  const needsPassword = user?.has_password !== false;
  const ready = /\S+@\S+\.\S+/.test(email) && confirm.trim().toUpperCase() === "TRANSFERER" && (!needsPassword || password) && !bot.auto_renew;

  const submit = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/bots/${bot.id}/transfer`, { email: email.trim(), password, confirm, new_license_key: newKey }, { headers: authHeader() });
      toast.success(`Produit transféré à ${r.data.to}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transfert impossible");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#12121a] border-white/10 rounded-2xl max-w-md" data-testid="transfer-dialog">
        <DialogHeader>
          <DialogTitle>Transférer {bot.bot_name}</DialogTitle>
          <DialogDescription className="text-white/50">
            Le produit passe sur le compte VantaPulse de l'autre personne. Ce n'est pas annulable : seule elle pourra te le redonner. Tes factures restent à ton nom.
          </DialogDescription>
        </DialogHeader>
        {bot.auto_renew ? (
          <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            Cet abonnement est payé chaque mois avec ta carte : arrête-le d'abord (bouton « Arrêter l'abonnement »), puis transfère le produit.
          </p>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-white/50">E-mail du compte VantaPulse qui reçoit le produit</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ami@exemple.fr" data-testid="transfer-email" className="mt-1 bg-black/30 border-white/10 rounded-lg" />
            </div>
            {!bot.downloadable && <p className="text-xs text-white/40">C'est un bot Discord : la personne doit avoir lié son Discord dans ses paramètres. Elle devient l'acheteur du bot.</p>}
            {bot.downloadable && (
              <label className="flex items-start gap-2 text-sm text-white/70 cursor-pointer">
                <input type="checkbox" checked={newKey} onChange={(e) => setNewKey(e.target.checked)} data-testid="transfer-new-key" className="mt-1" />
                <span>Générer une nouvelle clé de licence <span className="text-white/40">(recommandé : l'ancienne arrête de fonctionner sur ton serveur)</span></span>
              </label>
            )}
            {needsPassword && (
              <div>
                <label className="text-xs text-white/50">Ton mot de passe</label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" data-testid="transfer-password" className="mt-1 bg-black/30 border-white/10 rounded-lg" />
              </div>
            )}
            <div>
              <label className="text-xs text-white/50">Tape <span className="text-white font-mono">TRANSFERER</span> pour confirmer</label>
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="transfer-confirm" className="mt-1 bg-black/30 border-white/10 rounded-lg font-mono" />
            </div>
            <Button onClick={submit} disabled={!ready || sending} data-testid="transfer-submit" className="w-full rounded-lg bg-blue-600 hover:bg-blue-500">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" />Transférer le produit</>}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
